// src/components/Control/Features/BluetoothPanel.tsx
// Bluetooth-колонки/наушники робота (нода bt_manager на Pi):
// поиск, сопряжение, подключение, порядок приоритета и автоподключение.
//   robot/<id>/bt/state  <- состояние, robot/<id>/bt/cmd -> команды
import React, { useState } from 'react';
import { useZenohJson, putJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { BtDevice, BtState, FeatureProps, isStale, loadPref, savePref } from '../../../types/features';

function deviceIcon(d: BtDevice): string {
  if (d.icon.includes('headset') || d.icon.includes('headphone')) return '🎧';
  if (d.audio) return '🔊';
  if (d.icon.includes('phone')) return '📱';
  if (d.icon.includes('input')) return '⌨️';
  return '📶';
}

const BluetoothPanel: React.FC<FeatureProps> = ({ zenohSession, robotId }) => {
  const [state, setState] = useState<BtState | null>(null);
  const [audioOnly, setAudioOnly] = useState(() => loadPref('bt.audioOnly', true));
  const prefix = `robot/${robotId}/bt`;

  useTick(2000);

  // Ошибки в уведомления отправляет система событий страницы (robot/events.ts)
  useZenohJson<BtState>(zenohSession, `${prefix}/state`, msg => setState({ ...msg, timestamp: Date.now() }));

  const send = (cmd: Record<string, unknown>) => putJson(zenohSession, `${prefix}/cmd`, cmd);

  const online = state && !isStale(state.timestamp);
  const devices = state?.devices ?? [];
  const ranked = devices.filter(d => d.priority !== null).sort((a, b) => a.priority! - b.priority!);
  const others = devices
    .filter(d => d.priority === null && (!audioOnly || d.audio))
    .sort((a, b) => Number(b.paired) - Number(a.paired) || (b.rssi ?? -999) - (a.rssi ?? -999));
  const busy = !!state?.busy || !!state?.scanning;

  const move = (mac: string, delta: number) => {
    const order = ranked.map(d => d.mac);
    const i = order.indexOf(mac);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= order.length) return;
    [order[i], order[j]] = [order[j], order[i]];
    send({ cmd: 'priority', macs: order });
  };

  const addToList = (mac: string) => send({ cmd: 'priority', macs: [...ranked.map(d => d.mac), mac] });

  const status = (d: BtDevice) => {
    if (d.connected) return <span className="badge ok">подключено</span>;
    if (d.paired) return <span className="badge">сопряжено</span>;
    return <span className="badge off">не сопряжено</span>;
  };

  const connectButtons = (d: BtDevice) =>
    d.connected ? (
      <button className="btn-sm" disabled={busy} onClick={() => send({ cmd: 'disconnect', mac: d.mac })}>Отключить</button>
    ) : d.paired ? (
      <button className="btn-sm primary" disabled={busy} onClick={() => send({ cmd: 'connect', mac: d.mac })}>Подключить</button>
    ) : (
      <button className="btn-sm primary" disabled={busy} onClick={() => send({ cmd: 'pair', mac: d.mac })}>Сопрячь</button>
    );

  let adapterBadge = <span className="badge off">нет данных</span>;
  if (online && state) {
    adapterBadge = !state.adapter.ok
      ? <span className="badge alert">нет адаптера</span>
      : state.adapter.powered ? <span className="badge ok">включён</span> : <span className="badge warn">выключен</span>;
  }

  return (
    <div className="feature-card">
      <h3>
        <span>🔊 Bluetooth-звук</span>
        {adapterBadge}
      </h3>
      {!zenohSession && <p className="hint">Подключитесь к Zenoh</p>}
      {zenohSession && !online && (
        <p className="hint">Робот не присылает состояние Bluetooth (нода bt_manager не запущена?)</p>
      )}
      {state?.adapter.error && <p className="error-text">{state.adapter.error}</p>}

      <div className="checks">
        <label>
          <input type="checkbox" checked={!!state?.auto} disabled={!online}
            onChange={e => send({ cmd: 'auto', enabled: e.target.checked })} />
          Автоподключение
        </label>
        <label title="Если в зоне появилось устройство выше по списку — переключиться на него">
          <input type="checkbox" checked={!!state?.switch_higher} disabled={!online || !state?.auto}
            onChange={e => send({ cmd: 'switch_higher', enabled: e.target.checked })} />
          Переключаться на более приоритетное
        </label>
      </div>

      <h4>Мои устройства (по приоритету)</h4>
      {ranked.length === 0 ? (
        <p className="hint">Пусто. Найдите колонку и сопрягите её — она встанет в конец списка.</p>
      ) : (
        <ul className="device-list">
          {ranked.map((d, i) => (
            <li key={d.mac}>
              <span className="rank">{i + 1}</span>
              <span className="name" title={d.mac}>{deviceIcon(d)} {d.name}</span>
              {status(d)}
              {d.battery !== null && <span className="badge">🔋 {d.battery}%</span>}
              <span className="actions">
                <button className="btn-icon" title="Выше" disabled={i === 0} onClick={() => move(d.mac, -1)}>▲</button>
                <button className="btn-icon" title="Ниже" disabled={i === ranked.length - 1} onClick={() => move(d.mac, 1)}>▼</button>
                <label title="Подключать автоматически">
                  <input type="checkbox" checked={d.auto}
                    onChange={e => send({ cmd: 'device_auto', mac: d.mac, enabled: e.target.checked })} />
                  авто
                </label>
                {connectButtons(d)}
                <button className="btn-icon" title="Забыть устройство" disabled={busy}
                  onClick={() => window.confirm(`Забыть «${d.name}»? Потребуется новое сопряжение.`) && send({ cmd: 'forget', mac: d.mac })}>
                  🗑
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <h4>Найденные устройства</h4>
      <div className="row">
        <button className="btn-sm primary" disabled={!online || busy} onClick={() => send({ cmd: 'scan' })}>
          {state?.scanning ? 'Поиск…' : '🔍 Искать'}
        </button>
        <button className="btn-sm" disabled={!online || busy || ranked.length === 0} onClick={() => send({ cmd: 'autoconnect_now' })}>
          Подключить по приоритету
        </button>
        <label>
          <input type="checkbox" checked={audioOnly} onChange={e => { setAudioOnly(e.target.checked); savePref('bt.audioOnly', e.target.checked); }} />
          только аудио
        </label>
      </div>
      {state?.busy && <p className="hint">⏳ {state.busy}…</p>}
      {others.length === 0 ? (
        <p className="hint">{state?.scanning ? 'Ищем… Переведите колонку в режим сопряжения.' : 'Нет устройств — нажмите «Искать».'}</p>
      ) : (
        <ul className="device-list">
          {others.map(d => (
            <li key={d.mac}>
              <span className="name" title={d.mac}>{deviceIcon(d)} {d.name}</span>
              {status(d)}
              {d.rssi !== null && <span className="badge" title="Уровень сигнала">{d.rssi} dBm</span>}
              <span className="actions">
                {connectButtons(d)}
                <button className="btn-sm" title="Добавить в список приоритетов" onClick={() => addToList(d.mac)}>В список</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {state?.error && <p className="error-text">{state.error}</p>}
    </div>
  );
};

export default BluetoothPanel;
